import { useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { MotionStyle, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useBoolean } from '@chakra-ui/hooks';
import React from 'react';
import useDndStore from '../stores/dndStore';
import { useSortable } from '@dnd-kit/sortable';
import ItemTab from './ItemTab';
import ItemNote from './ItemNote';
import ItemStack from './ItemStack';
import useStore from '../stores/store';
import useSelectStore from '../stores/selectStore';
import useIsSelected from '../Utils/useIsSelected';
import layoutStore from '../stores/layoutStore';
import { CSS } from '@dnd-kit/utilities';
import { AlarmIndicator, MetaImageContainer, MotionBox } from './UiElements';
import usePersistStore from '../stores/persistStore';
import { switchToTab } from '../../platformFiles/browserAPI';
import { transition } from '@chakra-ui/react';
import ItemImage from './ItemImage';

interface ItemProps {
    data: {
        id: string;
        url: string;
        title: string;
        favIcon: string;
        dateCreated?: string;
        tabId?: string;
        hasAlarm?: any[] | undefined;
        metaUrl?: string;
        comment?: string;
        commentColor?: boolean;
        doneStatus?: boolean;
        justAdded?: boolean;
        note?: boolean;
        todo?: boolean;
        type?: string;
        groupColor?: string;
        emoji?: string;
        isStacked?: boolean;
        edited?: any;
        editor?: string;
        focus?: boolean;
        pinned?: boolean;
        imageUrl?: string;
    };
    isActiveTab?: boolean;
    groupId?: string;
    overlayItem?: boolean;
    insideActiveGroup?: string;
    isSortableDragging?: boolean;
}

export const DndItem = ({
    data,
    isActiveTab = false,
    groupId = 'active',
    overlayItem = false,
    insideActiveGroup = '',
    width
}) => {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: overlayItem ? data.id + 'overlay1' : data.id,
        data: {
            type: 'tab',
            groupId: groupId,
            data: data,
            isActiveTab,
            width
        }
    });
    const handlers = overlayItem ? {} : { ...attributes, ...listeners };
    // console.log('dnd item rerender', data.id);
    return (
        <div
            ref={overlayItem ? null : setNodeRef}
            {...handlers}
            style={{
                outline: 'none', //used to disable dnd focus
                //width: 'calc(100% - 24px)'
                margin: isActiveTab ? '' : '0px 12px' //'0px 4px 0px 12px'
            }}
        >
            {/* <MotionWrapper> */}
            <Item
                data={data}
                isActiveTab={isActiveTab}
                groupId={groupId}
                insideActiveGroup={insideActiveGroup}
                isSortableDragging={isDragging}
                overlayItem={overlayItem}
            />
            {/* </MotionWrapper> */}
        </div>
    );
};

const ItemWrapper = ({
    data,
    isActiveTab = false,
    groupId,
    insideActiveGroup = '',
    isSortableDragging = false,
    type,
    hover,
    hoverItem,
    loading,
    isDragging,
    overlayItem,
    children
}) => {
    const { id } = data;
    const isSelected = useSelectStore((state) =>
        state.selectedIds.includes(id)
    );
    const insertPosition = useDndStore((state) => state.insertPosition[id]);
    const itemDragConfirmed = useDndStore(
        (state) => state.dragItemsConfirmed[id]
    );
    // const hideDragIds = useDndStore((state) => state.hideDragIds[id]);
    const noteEditMode = layoutStore((state) => state.noteEditMode);
    const blockScaleUp = layoutStore((state) => state.blockScaleUp);
    const bgColor = useColorModeValue('#FFF', '#1e2123');
    const combineShadow = useColorModeValue(
        '0px 0px 0px 2px #44E18C',
        '0px 0px 0px 2px #44E18C'
    );
    /* const lightShadow = insideActiveGroup
        ? ''
        : '0 3px 8px 0px rgba(135, 145, 155, 0.3), 0 0px 0px 1px rgba(135, 145, 155, 0.08)';

    const darkShadow = insideActiveGroup
        ? ''
        : '0 3px 10px -3px rgba(0,0,0,0.90), 0 0px 0px 1px rgba(0, 0, 0, 0.2)'; */
    const draggingDropShadow = useColorModeValue(
        //'drop-shadow(0 14px 12px rgba(152,169,185,0.54))',
        'drop-shadow(0 14px 12px rgba(5, 9, 13, 0.24))',
        'drop-shadow(0 14px 12px rgba(0,0,0,0.65))'
    );

    const hoverShadow = useColorModeValue(
        '0 3px 8px 0px rgba(135, 145, 155, 0.3), 0 0px 0px 1px rgba(135, 145, 155, 0.08)',
        '0 3px 10px -3px rgba(0,0,0,0.90), 0 0px 0px 1px rgba(0, 0, 0, 0.2)'
    );
    const handleClick = (e) => {
        if (overlayItem) {
            return;
        }
        if (noteEditMode) return;
        if (
            isActiveTab &&
            e.type !== 'contextmenu' &&
            (e.detail === 2 || e.ctrlKey || e.button === 1)
        ) {
            switchToTab(id);
        } else {
            useSelectStore
                .getState()
                .handleSelect(
                    id,
                    e,
                    groupId,
                    isActiveTab ? 'active' : 'persisted'
                );
        }
    };

    // let itemSize = usePersistStore((state) => state.itemSize);
    // //itemSize is 'small' | 'medium' | 'large'
    // const itemPadding = {
    //     tab: isActiveTab ? '4px 10px' : '8px 10px',
    //     tabWithNote: '4px 10px 10px',
    //     note: '8px 10px',
    //     stack: '4px 10px'
    // };

    let itemSize = usePersistStore((state) => state.itemSize);

    const paddingMap = {
        small: {
            tab: isActiveTab ? '2px 12px' : '4px 10px',
            tabWithNote: '2px 10px 6px',
            note: '5px 10px',
            stack: '2px 10px 3px'
        },
        medium: {
            tab: isActiveTab ? '3px 12px' : '6px 10px',
            tabWithNote: '3px 10px 8px',
            note: '6px 10px',
            stack: '2px 10px 3px'
        },
        large: {
            tab: isActiveTab ? '4px 12px' : '8px 10px',
            tabWithNote: '4px 10px 10px',
            note: '8px 10px',
            stack: '4px 10px'
        }
    };

    const itemPadding = paddingMap[itemSize];

    const innerNoteIsFocused = noteEditMode === id;
    const itemIsLoading = false; //useStore((state) => state.loadingItems[id]);

    const shouldNotHover =
        isSelected || isDragging || innerNoteIsFocused || blockScaleUp;

    let isValidHover =
        hoverItem &&
        !isDragging &&
        !innerNoteIsFocused &&
        !blockScaleUp &&
        !loading &&
        !overlayItem;

    const itemWrapperStyles = {
        backgroundColor:
            hoverItem || overlayItem
                ? insideActiveGroup
                    ? insideActiveGroup
                    : bgColor
                : 'transparent',
        border: 'none',
        display: 'flex',
        flexDirection:
        type === 'tabWithNote' || type === 'tabWithMeta' || type === 'image' ? 'column' : 'row',
        pointerEvents: itemIsLoading ? 'none' : 'auto',
        position: 'relative',
        cursor: overlayItem ? 'grabbing' : 'grab',
        padding: itemPadding[type] || itemPadding['tab'],
        alignItems:
            type === 'tabWithNote' || type === 'note' ? 'start' : 'center',
        margin: '2px 0',
        borderRadius: '10px',
        minWidth: '52px',
        filter: overlayItem ? draggingDropShadow : 'none'
    };

    const initial = {
        boxShadow:
            (isDragging && itemDragConfirmed) ||
            overlayItem ||
            isSelected ||
            hoverItem ||
            insertPosition === 'center'
                ? combineShadow
                : '',
        filter: overlayItem
            ? draggingDropShadow
            : 'drop-shadow(0px 0px 0px rgba(0, 0, 0, 0))'
    };

    const animate = {
        opacity: (isDragging && itemDragConfirmed) || itemIsLoading ? 0.5 : 1,
        boxShadow:
            (isDragging && itemDragConfirmed) ||
            overlayItem ||
            isSelected ||
            insertPosition === 'center'
                ? 'inset 0px 0px 0px 2px #44E18C'
                : isValidHover
                ? hoverShadow
                : '',
        zIndex: hoverItem && !shouldNotHover ? 300 : 1,
        filter: overlayItem
            ? draggingDropShadow
            : 'drop-shadow(0px 0px 0px rgba(0, 0, 0, 0))'
    };

    const whileTap = {
        //zIndex: 301,
        //originY: 1,
        scale: isDragging || innerNoteIsFocused || blockScaleUp ? 1 : 1.0225,
        cursor: 'grabbing',
        boxShadow: isValidHover ? 'inset 0px 0px 0px 2px #44E18C' : '',
        filter: isValidHover ? draggingDropShadow : '',
        transition: {
            delay: 0.1,
            duration: 0.12,
            filter: { duration: 0.12, delay: 0.1 }
        }
    };

    return (
        <>
            <motion.div
                style={itemWrapperStyles as MotionStyle}
                initial={initial}
                animate={animate}
                whileTap={whileTap}
                //key={id}
                id={id + 'dropTarget'}
                onMouseEnter={hover.on}
                onMouseOver={hover.on}
                onMouseLeave={hover.off}
                onClick={(e) => handleClick(e)}
                transition={{ duration: 0.1 }}
            >
                {children}
                <InsertIndicatorLine side={insertPosition} />
            </motion.div>
        </>
    );
};

export const Item: React.FC<ItemProps> = React.memo(
    ({
        data,
        isActiveTab = false,
        groupId,
        insideActiveGroup = '',
        isSortableDragging = false,
        overlayItem
    }) => {
        const { id } = data;
        const [loading, setLoadingState] = useState(false);
        const isDragging = useDndStore((state) => state.dragIds.includes(id));

        const [hoverItem, hover] = useBoolean();

        const type = data.imageUrl
            ? 'image'
            : data.url
                ? data.comment?.length
                    ? 'tabWithNote'
                : data.metaUrl
                ? 'tabWithMeta'
                : 'tab'
            : data.isStacked
            ? 'stack'
            : 'note';

        const innerItem = {
            image: (
                <>
                    <ItemImage
                        groupId={groupId}
                    id={id}
                    data={data}
                    hoverItem={hoverItem}
                    />
                    {data.comment && data.comment.length > 0 && (
                        <span
                            style={{
                                display: 'flex',
                            maxWidth: '100%',
                            minWidth: '100%',
                            marginTop: '2px'
                        }}
                    >
                        <ItemNote
                            groupId={groupId}
                            id={id}
                            currentColor={data.commentColor}
                            comment={data.comment}
                            isTodo={data.todo}
                            doneStatus={data.doneStatus}
                            justAdded={data.justAdded}
                            hasAlarm={data.hasAlarm}
                            edited={data.edited}
                            editor={data.editor}
                            type={data.type}
                            hoverNote={hoverItem}
                            hasUrl={true}
                            overlayItem={overlayItem}
                        />
                    </span>
                )}
                </>
            ),
            tab: (
                <ItemTab
                    groupId={groupId}
                    data={data}
                    isActiveTab={isActiveTab}
                    insideActiveGroup={insideActiveGroup}
                    hoverSite={hoverItem}
                    hover={hover}
                    loading={loading}
                    setLoadingState={setLoadingState}
                    hideTooltip={isDragging || overlayItem}
                    overlayItem={overlayItem}
                    hasAlarm={data.hasAlarm}
                    isPinned={data.pinned}
                    //isDiscarded={data.discarded}
                />
            ),
            note: (
                <ItemNote
                    groupId={groupId}
                    id={id}
                    currentColor={data.commentColor}
                    comment={data.comment}
                    isTodo={data.todo}
                    doneStatus={data.doneStatus}
                    justAdded={data.justAdded}
                    hasAlarm={data.hasAlarm}
                    edited={data.edited}
                    editor={data.editor}
                    type={data.type}
                    hoverNote={hoverItem}
                    hasUrl={false}
                    overlayItem={overlayItem}
                />
            ),
            tabWithNote: (
                <>
                    {data.metaUrl && (
                        <MetaImageContainer
                            metaUrl={data.metaUrl}
                            hover={hoverItem || isDragging}
                        />
                    )}
                    <Flex maxW="100%" minW="100%" py="4px">
                        <ItemTab
                            groupId={groupId}
                            data={data}
                            isActiveTab={isActiveTab}
                            insideActiveGroup={insideActiveGroup}
                            hoverSite={hoverItem}
                            loading={loading}
                            setLoadingState={setLoadingState}
                            overlayItem={overlayItem}
                            hasAlarm={data.hasAlarm}
                        />
                    </Flex>
                    <span
                        style={{
                            display: 'flex',
                            maxWidth: '100%',
                            minWidth: '100%'
                        }}
                    >
                        <ItemNote
                            groupId={groupId}
                            id={id}
                            currentColor={data.commentColor}
                            comment={data.comment}
                            isTodo={data.todo}
                            doneStatus={data.doneStatus}
                            justAdded={data.justAdded}
                            hasAlarm={data.hasAlarm}
                            edited={data.edited}
                            editor={data.editor}
                            type={data.type}
                            hoverNote={hoverItem}
                            hasUrl={true}
                            overlayItem={overlayItem}
                        />
                    </span>
                </>
            ),
            stack: (
                <ItemStack
                    data={data}
                    groupId={groupId}
                    hoverList={hoverItem}
                    setLoadingState={setLoadingState}
                    loading={loading}
                    isDragging={isDragging}
                    overlayItem={overlayItem}
                />
            ),
            tabWithMeta: (
                <>
                    <MetaImageContainer
                        metaUrl={data.metaUrl}
                        hover={hoverItem || isDragging}
                    />
                    <Flex maxW="100%" minW="100%" py="4px">
                        <ItemTab
                            groupId={groupId}
                            data={data}
                            isActiveTab={isActiveTab}
                            insideActiveGroup={insideActiveGroup}
                            hoverSite={hoverItem}
                            loading={loading}
                            setLoadingState={setLoadingState}
                            overlayItem={overlayItem}
                            hasAlarm={data.hasAlarm}
                        />
                    </Flex>
                </>
            )
        };

        return (
            <ItemWrapper
                data={data}
                isActiveTab={isActiveTab}
                groupId={groupId}
                insideActiveGroup={insideActiveGroup}
                isSortableDragging={isSortableDragging}
                type={type}
                hover={hover}
                hoverItem={hoverItem}
                loading={loading}
                isDragging={isDragging}
                overlayItem={overlayItem}
            >
                {innerItem[type] || innerItem['tab']}
            </ItemWrapper>
        );
    }
);

export const InsertIndicatorLine = ({ side }) => {
    const isOver = side === 'over';
    const isUnder = side === 'under';

    return (
        <>
            {isOver && (
                <motion.div
                    //initial={{ scaleX: 0.875 }}
                    //animate={{ scaleX: 1 }}
                    style={{
                        position: 'absolute',
                        top: '-2px',
                        left: 0,
                        right: 0,
                        borderRadius: '1px',
                        height: '2px',
                        width: '100%',
                        backgroundColor: '#44e18c'
                    }}
                />
            )}
            {isUnder && (
                <motion.div
                    //initial={{ scaleX: 0.875 }}
                    //animate={{ scaleX: 1 }}
                    style={{
                        position: 'absolute',
                        bottom: '-2px',
                        left: 0,
                        right: 0,
                        height: '2px',
                        borderRadius: '1px',
                        width: '100%',
                        backgroundColor: '#44e18c'
                    }}
                />
            )}
        </>
    );
};

// export const MotionWrapper = ({ children }) => {
//     const isDropping = useDndStore((state) => state.isDropping);
//     return (
//         <motion.div
//             layout={false} //prev: "position" -> Set to false on FEB29 to prevent workspace layout shift
//             transition={{
//                 layout: {
//                     duration: isDropping ? 0.2 : 0
//                 }
//             }}
//             //whileTap={{ scale: overlayItem ? 1.0225 : 1 }}
//         >
//             {children}
//         </motion.div>
//     );
// };
